import { SESSION_AUTH_TIME_STORAGE_KEY } from 'config'
import { tryStorageAccess } from './tryStorageAccess'

// The tabs of a browser share the session, but Firebase passes a renewed token
// to the other tabs by polling, which the browser slows down in background tabs.
// The auth time is also shared through localStorage, which every tab reads at
// once, so a tab that hasn't got the renewed token yet doesn't end the session.
interface SharedAuthTime {
  uid: string
  authTime: string
}

// Changes when any tab shares a newer auth time or signs out
export const readSharedAuthTimeEntry = () =>
  tryStorageAccess(() =>
    window.localStorage.getItem(SESSION_AUTH_TIME_STORAGE_KEY)
  ) ?? null

export const getSharedAuthTime = (uid: string) => {
  try {
    const shared: SharedAuthTime | null = JSON.parse(
      readSharedAuthTimeEntry() ?? 'null'
    )
    return shared?.uid === uid ? shared.authTime : undefined
  } catch {
    return undefined
  }
}

// The newer of the given auth time and the one shared by any tab
export const getNewestAuthTime = (uid: string, authTime: string) => {
  const sharedAuthTime = getSharedAuthTime(uid)

  return sharedAuthTime && Date.parse(sharedAuthTime) > Date.parse(authTime)
    ? sharedAuthTime
    : authTime
}

export const shareAuthTime = (uid: string, authTime: string) => {
  if (getNewestAuthTime(uid, authTime) !== authTime) return

  tryStorageAccess(() =>
    window.localStorage.setItem(
      SESSION_AUTH_TIME_STORAGE_KEY,
      JSON.stringify({ uid, authTime })
    )
  )
}

export const clearSharedAuthTime = () =>
  tryStorageAccess(() =>
    window.localStorage.removeItem(SESSION_AUTH_TIME_STORAGE_KEY)
  )
